import { useState } from "react";
import { ArrowUpRight, Minus, Plus } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { buildBookingUrl, defaultDates, toISODate, trackBookingClick } from "@/lib/booking";

function Counter({ label, value, onChange, min, testId }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="field-label">{label}</span>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`- ${label}`} data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(min, value - 1))} className="flex h-7 w-7 items-center justify-center border border-line-md text-charcoal/70 hover:border-charcoal hover:text-charcoal transition-colors"><Minus size={12} /></button>
        <span data-testid={`${testId}-value`} className="w-5 text-center font-serif text-xl">{value}</span>
        <button type="button" aria-label={`+ ${label}`} data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(9, value + 1))} className="flex h-7 w-7 items-center justify-center border border-line-md text-charcoal/70 hover:border-charcoal hover:text-charcoal transition-colors"><Plus size={12} /></button>
      </div>
    </div>
  );
}

export function BookingBar({ className = "" }) {
  const { t, lang } = useLang();
  const d = defaultDates();
  const [checkin, setCheckin] = useState(d.checkin);
  const [checkout, setCheckout] = useState(d.checkout);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [promo, setPromo] = useState("");

  const onCheckin = (v) => {
    setCheckin(v);
    if (v >= checkout) {
      const n = new Date(v);
      n.setDate(n.getDate() + 1);
      setCheckout(toISODate(n));
    }
  };

  const url = buildBookingUrl({ lang, checkin, checkout, adults, children, promo });
  const today = toISODate(new Date());

  return (
    <div data-testid="hero-booking-bar" className={`bg-sand text-charcoal shadow-[0_30px_80px_-30px_rgba(20,19,18,0.45)] ${className}`}>
      <div className="grid grid-cols-2 md:grid-cols-[1.1fr_1.1fr_0.9fr_0.9fr_1fr_auto] items-end gap-6 md:gap-5 px-6 md:px-8 py-6 md:py-7">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="bb-checkin" className="field-label">{t.hero.checkin}</label>
          <input id="bb-checkin" data-testid="booking-checkin" type="date" min={today} value={checkin} onChange={(e) => onCheckin(e.target.value)} className="field !py-2 font-serif text-lg" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="bb-checkout" className="field-label">{t.hero.checkout}</label>
          <input id="bb-checkout" data-testid="booking-checkout" type="date" min={checkin} value={checkout} onChange={(e) => setCheckout(e.target.value)} className="field !py-2 font-serif text-lg" />
        </div>
        <Counter label={t.hero.adults} value={adults} onChange={setAdults} min={1} testId="booking-adults" />
        <Counter label={t.hero.children} value={children} onChange={setChildren} min={0} testId="booking-children" />
        <div className="flex flex-col gap-1.5 col-span-2 md:col-span-1">
          <label htmlFor="bb-promo" className="field-label">{t.hero.promo}</label>
          <input id="bb-promo" data-testid="booking-promo" type="text" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={t.hero.promoPlaceholder} className="field !py-2 font-mono text-xs uppercase tracking-[0.15em]" />
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid="booking-submit" onClick={() => trackBookingClick("booking-submit", lang)} className="btn btn-accent col-span-2 md:col-span-1 w-full md:w-auto !py-4">
          {t.hero.availability}
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </a>
      </div>
      <div className="border-t border-line px-6 md:px-8 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-terra">{t.hero.bestPrice}</div>
    </div>
  );
}
